import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  AlertTriangle,
  FileEdit,
  MessageCircleQuestion,
  ChevronRight,
  Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

const benefits = [
  {
    icon: AlertTriangle,
    title: "Flags ambiguity and missing assumptions",
  },
  {
    icon: FileEdit,
    title: "Rewrites requirements for clarity",
  },
  {
    icon: MessageCircleQuestion,
    title: "Generates clarifying questions",
  },
];

const phrases = ["unclear requirements", "sprint delays", "ambiguous specs", "scope creep"];

function TypewriterText() {
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [deleting, setDeleting] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const current = phrases[phraseIdx];
    if (!deleting && displayed.length < current.length) {
      timeoutRef.current = setTimeout(() => setDisplayed(current.slice(0, displayed.length + 1)), 60);
    } else if (!deleting && displayed.length === current.length) {
      timeoutRef.current = setTimeout(() => setDeleting(true), 2000);
    } else if (deleting && displayed.length > 0) {
      timeoutRef.current = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 35);
    } else if (deleting && displayed.length === 0) {
      setDeleting(false);
      setPhraseIdx((i) => (i + 1) % phrases.length);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [displayed, deleting, phraseIdx]);

  return (
    <span className="text-gradient relative">
      {displayed}
      <span className="animate-pulse">|</span>
    </span>
  );
}

export function HeroSection() {
  return (
    <section className="relative min-h-[85vh] flex items-center overflow-hidden">
      {/* Primary glow — top center */}
      <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[700px] h-[450px] rounded-full bg-primary/[0.10] blur-[140px]" />

      {/* Accent glow — bottom left */}
      <div
        className="absolute bottom-[-10%] left-[15%] w-[400px] h-[400px] rounded-full bg-accent/[0.06] blur-[120px]"
        style={{ animationDelay: "2s" }}
      />

      {/* Noise texture */}
      <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(hsl(var(--primary) / 0.4) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary) / 0.4) 1px, transparent 1px)`,
          backgroundSize: "80px 80px",
        }}
      />

      {/* Top edge line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      {/* Content */}
      <div className="container relative z-10 px-4 md:px-6 py-16 md:py-24">
        <div className="max-w-4xl mx-auto text-center">
          {/* Live badge */}
          <Link
            to="/beta"
            className="hero-stagger-1 group inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-primary/20 bg-primary/[0.06] backdrop-blur-md text-sm mb-8 transition-all duration-300 hover:border-primary/40 hover:bg-primary/[0.10]"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-reqqy-success opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-reqqy-success" />
            </span>
            <span className="text-muted-foreground tracking-wide group-hover:text-foreground transition-colors">AI-powered · Now in beta</span>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5" />
          </Link>

          {/* Headline — tighter sizing */}
          <h1 className="hero-stagger-2 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-bold tracking-[-0.03em] mb-5 leading-[1.05]">
            Stop shipping
            <br />
            <TypewriterText />
          </h1>

          {/* Subheading — narrower max-width */}
          <p className="hero-stagger-3 text-base md:text-lg lg:text-xl text-muted-foreground max-w-xl mx-auto mb-8 leading-relaxed">
            Reqqy reviews your requirements and surfaces what’s unclear, incomplete, or not testable before it reaches development.{" "}
            <span className="text-foreground font-medium">No more "what do you mean by...?" in sprint planning.</span>
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 animate-slide-up">
            <Button
              asChild
              size="lg"
              className="relative group h-11 px-7 text-sm font-semibold gradient-primary text-primary-foreground overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_-8px_hsl(var(--primary)/0.5)]"
            >
              <Link to="/dashboard/new-run?mode=review">
                <span className="relative z-10 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Review my requirements — free
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-11 px-7 text-sm border-border/60 hover:border-primary/40 transition-all duration-300">
              <Link to="/pricing">See Pricing</Link>
            </Button>
          </div>

          {/* Trust signals */}
          <p className="hero-stagger-5 text-xs text-muted-foreground/70 mt-4 tracking-wide">
            No credit card required · 10 free credits · Setup in 30 seconds
          </p>

          {/* Benefits row */}
          <div className="hero-stagger-6 mt-12">
            <div className="grid sm:grid-cols-3 gap-3 max-w-2xl mx-auto">
              {benefits.map((benefit) => (
                <div
                  key={benefit.title}
                  className="group flex items-start gap-2.5 p-3.5 rounded-lg border border-border/30 bg-card/[0.4] backdrop-blur-md text-left transition-all duration-300 hover:border-primary/20 hover:bg-card/[0.6]"
                >
                  <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <benefit.icon className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <p className="text-xs font-medium leading-snug text-foreground/90">{benefit.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
}
